// Make + model -> body type key. Keys here MUST match the backend
// `VehicleBodyType` enum (the one `EnumService.getVehicleBodyTypes()` returns)
// — that's what populates the body-type dropdown. The newer `BodyType` enum
// is broader but isn't what the dropdown is fed today.
//
// Used to pre-fill the body type when a known model is picked. Lookup is
// case-insensitive and trims surrounding whitespace; user can still override.

type BodyTypeKey =
  | 'SALOON_SEDAN'
  | 'HATCHBACK'
  | 'COUPE'
  | 'CONVERTIBLE'
  | 'STATION_WAGON'
  | 'SUV'
  | 'VAN'
  | 'PICKUP'
  | 'SPORTS_CAR'
  | 'MAXI_BIG_BUS'
  | 'MINI_SMALL_BUS'
  | 'MOTORCYCLE'
  | 'ARTICULATED_TRUCK';

type ModelMap = Record<string, BodyTypeKey>;

const VEHICLE_BODY_TYPE_BY_MAKE: Record<string, ModelMap> = {
  TOYOTA: {
    CAMRY: 'SALOON_SEDAN',
    COROLLA: 'SALOON_SEDAN',
    YARIS: 'SALOON_SEDAN',
    AVENSIS: 'SALOON_SEDAN',
    AVALON: 'SALOON_SEDAN',
    VITZ: 'HATCHBACK',
    AYGO: 'HATCHBACK',
    'COROLLA HATCHBACK': 'HATCHBACK',
    HILUX: 'PICKUP',
    TACOMA: 'PICKUP',
    TUNDRA: 'PICKUP',
    'LAND CRUISER': 'SUV',
    'LAND CRUISER PRADO': 'SUV',
    PRADO: 'SUV',
    FORTUNER: 'SUV',
    RAV4: 'SUV',
    HIGHLANDER: 'SUV',
    '4RUNNER': 'SUV',
    SEQUOIA: 'SUV',
    'C-HR': 'SUV',
    SIENNA: 'VAN',
    HIACE: 'VAN',
    COASTER: 'MAXI_BIG_BUS',
  },
  HONDA: {
    CIVIC: 'SALOON_SEDAN',
    ACCORD: 'SALOON_SEDAN',
    CITY: 'SALOON_SEDAN',
    INSIGHT: 'SALOON_SEDAN',
    'CR-V': 'SUV',
    CRV: 'SUV',
    'HR-V': 'SUV',
    HRV: 'SUV',
    PILOT: 'SUV',
    PASSPORT: 'SUV',
    FIT: 'HATCHBACK',
    JAZZ: 'HATCHBACK',
    ODYSSEY: 'VAN',
    RIDGELINE: 'PICKUP',
  },
  NISSAN: {
    SENTRA: 'SALOON_SEDAN',
    ALMERA: 'SALOON_SEDAN',
    SUNNY: 'SALOON_SEDAN',
    MAXIMA: 'SALOON_SEDAN',
    ALTIMA: 'SALOON_SEDAN',
    VERSA: 'SALOON_SEDAN',
    'X-TRAIL': 'SUV',
    XTRAIL: 'SUV',
    ROGUE: 'SUV',
    MURANO: 'SUV',
    PATHFINDER: 'SUV',
    PATROL: 'SUV',
    ARMADA: 'SUV',
    JUKE: 'SUV',
    KICKS: 'SUV',
    QASHQAI: 'SUV',
    NAVARA: 'PICKUP',
    FRONTIER: 'PICKUP',
    TITAN: 'PICKUP',
    QUEST: 'VAN',
    SERENA: 'VAN',
    NOTE: 'HATCHBACK',
    MICRA: 'HATCHBACK',
    NV200: 'VAN',
  },
  HYUNDAI: {
    ELANTRA: 'SALOON_SEDAN',
    SONATA: 'SALOON_SEDAN',
    ACCENT: 'SALOON_SEDAN',
    AZERA: 'SALOON_SEDAN',
    GENESIS: 'SALOON_SEDAN',
    TUCSON: 'SUV',
    'SANTA FE': 'SUV',
    PALISADE: 'SUV',
    KONA: 'SUV',
    CRETA: 'SUV',
    VENUE: 'SUV',
    I10: 'HATCHBACK',
    I20: 'HATCHBACK',
    I30: 'HATCHBACK',
    H1: 'VAN',
    STARIA: 'VAN',
  },
  KIA: {
    RIO: 'SALOON_SEDAN',
    CERATO: 'SALOON_SEDAN',
    OPTIMA: 'SALOON_SEDAN',
    FORTE: 'SALOON_SEDAN',
    K5: 'SALOON_SEDAN',
    SPORTAGE: 'SUV',
    SORENTO: 'SUV',
    SELTOS: 'SUV',
    TELLURIDE: 'SUV',
    SOUL: 'SUV',
    PICANTO: 'HATCHBACK',
    CARNIVAL: 'VAN',
    SEDONA: 'VAN',
  },
  FORD: {
    FUSION: 'SALOON_SEDAN',
    FOCUS: 'SALOON_SEDAN',
    MUSTANG: 'COUPE',
    ESCAPE: 'SUV',
    EDGE: 'SUV',
    EXPLORER: 'SUV',
    EXPEDITION: 'SUV',
    BRONCO: 'SUV',
    ECOSPORT: 'SUV',
    'F-150': 'PICKUP',
    F150: 'PICKUP',
    'F-250': 'PICKUP',
    F250: 'PICKUP',
    RANGER: 'PICKUP',
    TRANSIT: 'VAN',
    'TRANSIT CONNECT': 'VAN',
  },
  MITSUBISHI: {
    LANCER: 'SALOON_SEDAN',
    GALANT: 'SALOON_SEDAN',
    OUTLANDER: 'SUV',
    PAJERO: 'SUV',
    MONTERO: 'SUV',
    ASX: 'SUV',
    ECLIPSE: 'COUPE',
    'ECLIPSE CROSS': 'SUV',
    L200: 'PICKUP',
    TRITON: 'PICKUP',
  },
  SUZUKI: {
    SWIFT: 'HATCHBACK',
    ALTO: 'HATCHBACK',
    CELERIO: 'HATCHBACK',
    BALENO: 'HATCHBACK',
    SX4: 'SUV',
    VITARA: 'SUV',
    'GRAND VITARA': 'SUV',
    JIMNY: 'SUV',
    ERTIGA: 'VAN',
  },
  MAZDA: {
    '2': 'HATCHBACK',
    '3': 'SALOON_SEDAN',
    '6': 'SALOON_SEDAN',
    'CX-3': 'SUV',
    CX3: 'SUV',
    'CX-30': 'SUV',
    'CX-5': 'SUV',
    CX5: 'SUV',
    'CX-7': 'SUV',
    'CX-9': 'SUV',
    CX9: 'SUV',
    'BT-50': 'PICKUP',
    BT50: 'PICKUP',
  },
  'MERCEDES-BENZ': {
    'A-CLASS': 'HATCHBACK',
    'B-CLASS': 'STATION_WAGON',
    'C-CLASS': 'SALOON_SEDAN',
    'E-CLASS': 'SALOON_SEDAN',
    'S-CLASS': 'SALOON_SEDAN',
    'CLA': 'SALOON_SEDAN',
    'CLS': 'SALOON_SEDAN',
    GLA: 'SUV',
    GLB: 'SUV',
    GLC: 'SUV',
    GLE: 'SUV',
    GLS: 'SUV',
    'G-CLASS': 'SUV',
    SPRINTER: 'VAN',
    VITO: 'VAN',
    VIANO: 'VAN',
  },
  MERCEDES: {
    'A-CLASS': 'HATCHBACK',
    'C-CLASS': 'SALOON_SEDAN',
    'E-CLASS': 'SALOON_SEDAN',
    'S-CLASS': 'SALOON_SEDAN',
    GLC: 'SUV',
    GLE: 'SUV',
    GLS: 'SUV',
    SPRINTER: 'VAN',
  },
  BMW: {
    '1 SERIES': 'HATCHBACK',
    '2 SERIES': 'COUPE',
    '3 SERIES': 'SALOON_SEDAN',
    '4 SERIES': 'COUPE',
    '5 SERIES': 'SALOON_SEDAN',
    '6 SERIES': 'COUPE',
    '7 SERIES': 'SALOON_SEDAN',
    '8 SERIES': 'COUPE',
    X1: 'SUV',
    X2: 'SUV',
    X3: 'SUV',
    X4: 'SUV',
    X5: 'SUV',
    X6: 'SUV',
    X7: 'SUV',
    Z4: 'CONVERTIBLE',
    M3: 'SALOON_SEDAN',
    M5: 'SALOON_SEDAN',
  },
  AUDI: {
    A1: 'HATCHBACK',
    A3: 'SALOON_SEDAN',
    A4: 'SALOON_SEDAN',
    A5: 'COUPE',
    A6: 'SALOON_SEDAN',
    A7: 'SALOON_SEDAN',
    A8: 'SALOON_SEDAN',
    Q2: 'SUV',
    Q3: 'SUV',
    Q5: 'SUV',
    Q7: 'SUV',
    Q8: 'SUV',
    TT: 'COUPE',
  },
  VOLKSWAGEN: {
    GOLF: 'HATCHBACK',
    POLO: 'HATCHBACK',
    JETTA: 'SALOON_SEDAN',
    PASSAT: 'SALOON_SEDAN',
    ARTEON: 'SALOON_SEDAN',
    TIGUAN: 'SUV',
    TOUAREG: 'SUV',
    ATLAS: 'SUV',
    'T-ROC': 'SUV',
    AMAROK: 'PICKUP',
    CADDY: 'VAN',
    TRANSPORTER: 'VAN',
  },
  VW: {
    GOLF: 'HATCHBACK',
    POLO: 'HATCHBACK',
    JETTA: 'SALOON_SEDAN',
    PASSAT: 'SALOON_SEDAN',
    TIGUAN: 'SUV',
    TOUAREG: 'SUV',
  },
  LEXUS: {
    ES: 'SALOON_SEDAN',
    IS: 'SALOON_SEDAN',
    LS: 'SALOON_SEDAN',
    NX: 'SUV',
    RX: 'SUV',
    GX: 'SUV',
    LX: 'SUV',
    UX: 'SUV',
  },
  CHEVROLET: {
    SPARK: 'HATCHBACK',
    AVEO: 'HATCHBACK',
    SONIC: 'HATCHBACK',
    CRUZE: 'SALOON_SEDAN',
    MALIBU: 'SALOON_SEDAN',
    IMPALA: 'SALOON_SEDAN',
    CAMARO: 'COUPE',
    CORVETTE: 'COUPE',
    EQUINOX: 'SUV',
    TRAILBLAZER: 'SUV',
    TAHOE: 'SUV',
    SUBURBAN: 'SUV',
    TRAVERSE: 'SUV',
    SILVERADO: 'PICKUP',
    COLORADO: 'PICKUP',
  },
  PEUGEOT: {
    '208': 'HATCHBACK',
    '301': 'SALOON_SEDAN',
    '308': 'HATCHBACK',
    '508': 'SALOON_SEDAN',
    '2008': 'SUV',
    '3008': 'SUV',
    '5008': 'SUV',
  },
  RENAULT: {
    LOGAN: 'SALOON_SEDAN',
    SANDERO: 'HATCHBACK',
    DUSTER: 'SUV',
    KOLEOS: 'SUV',
    CAPTUR: 'SUV',
  },
  ISUZU: {
    'D-MAX': 'PICKUP',
    DMAX: 'PICKUP',
    'MU-X': 'SUV',
  },
  LANDROVER: {
    DEFENDER: 'SUV',
    DISCOVERY: 'SUV',
    'DISCOVERY SPORT': 'SUV',
    'RANGE ROVER': 'SUV',
    'RANGE ROVER SPORT': 'SUV',
    'RANGE ROVER EVOQUE': 'SUV',
    'RANGE ROVER VELAR': 'SUV',
  },
  'LAND ROVER': {
    DEFENDER: 'SUV',
    DISCOVERY: 'SUV',
    'RANGE ROVER': 'SUV',
    'RANGE ROVER SPORT': 'SUV',
    'RANGE ROVER EVOQUE': 'SUV',
  },
  JEEP: {
    WRANGLER: 'SUV',
    GRAND_CHEROKEE: 'SUV',
    CHEROKEE: 'SUV',
    COMPASS: 'SUV',
    RENEGADE: 'SUV',
    GLADIATOR: 'PICKUP',
  },
  ACURA: {
    ILX: 'SALOON_SEDAN',
    TLX: 'SALOON_SEDAN',
    RDX: 'SUV',
    MDX: 'SUV',
  },
  INFINITI: {
    Q50: 'SALOON_SEDAN',
    Q60: 'COUPE',
    Q70: 'SALOON_SEDAN',
    QX50: 'SUV',
    QX60: 'SUV',
    QX80: 'SUV',
  },
  SUBARU: {
    IMPREZA: 'SALOON_SEDAN',
    LEGACY: 'SALOON_SEDAN',
    OUTBACK: 'STATION_WAGON',
    FORESTER: 'SUV',
    ASCENT: 'SUV',
    CROSSTREK: 'SUV',
  },
};

const normalize = (s: string | null | undefined) =>
  String(s ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ');

export const lookupVehicleBodyType = (
  make: string | null | undefined,
  model: string | null | undefined,
): string | null => {
  const m = normalize(make);
  const md = normalize(model);
  if (!m || !md) return null;
  const direct = VEHICLE_BODY_TYPE_BY_MAKE[m]?.[md];
  if (direct) return direct;
  // Fallback: try a model match using the first token (e.g. "CAMRY LE" -> "CAMRY")
  const firstToken = md.split(' ')[0];
  if (firstToken && firstToken !== md) {
    const fallback = VEHICLE_BODY_TYPE_BY_MAKE[m]?.[firstToken];
    if (fallback) return fallback;
  }
  return null;
};
